<template>
  <div>
    <CandidateInterviewProcessing v-if="isOfficeInterviewProcessing" />
    <CandidateInterviewProcessingFailed v-else-if="isOfficeInterviewProcessingFailed" />
    <VCard
      v-else-if="!readonly && showOfficeInterviewButtons"
      flat
      class="d-flex flex-column justify-center align-center grey lighten-4 fill-height"
      width="100%"
    >
      <span class="subitle-1 text-center font-weight-medium">
        {{ $t('recordWasStopped') }}
      </span>
      <span class="body-2 text-center mt-2 mb-6 grey--text text--darken-1">
        {{ $t('chooseOption') }}:
      </span>
      <VBtn
        color="success"
        width="200"
        class="mb-2"
        @click.stop="resumeOfficeInterview"
      >
        <VIcon class="mr-2">
          mdi-play
        </VIcon>
        {{ $t('resumeOfficeInterview') }}
      </VBtn>
      <VBtn
        color="success"
        width="200"
        outlined
        class="mb-6"
        @click.stop="overwriteOfficeInterview"
      >
        <VIcon small class="mr-2">
          mdi-sync
        </VIcon>
        {{ $t('overwrite') }}
      </VBtn>
      <VBtn
        color="grey"
        text
        width="200"
        elevation="0"
        :loading="officeInterviewPrepareLoading"
        @click.stop="saveOfficeInterview"
      >
        <VIcon small class="mr-2">
          mdi-content-save
        </VIcon>
        {{ $t('save') }}
      </VBtn>
    </VCard>
    <template v-else-if="showOfficeInterviewPlayer">
      <template v-if="candidate.quotaWrittenOff">
        <CandidateInterviewOffice
          v-model="officeInterviewPlayerTime"
          :interview="interview"
        />
      </template>
      <template v-else>
        <InterviewsQuotaExceededCandidate>
          <AppWithLoading :fetch="openCurrentCandidate">
            <CandidateInterviewOffice
              v-model="officeInterviewPlayerTime"
              :interview="interview"
            />
          </AppWithLoading>
        </InterviewsQuotaExceededCandidate>
      </template>
    </template>
    <VDialog
      v-if="officeInterviewDialog"
      v-model="officeInterviewDialog"
      persistent
      :max-width="900"
    >
      <VCard>
        <DialogHeader v-if="showOfficeInterviewCloseButton" @close="closeOfficeInterviewDialog" />
        <VCardText>
          <OfficeInterviewRecorderForm
            :vacancy="vacancy"
            :candidate="candidate"
            :resume="officeInterviewResume"
            :overwrite="officeInterviewOverwrite"
            @started="interviewRecordingStarted"
            @stopped="interviewRecordingFinished"
            @error="interviewRecordingError"
          />
        </VCardText>
      </VCard>
    </VDialog>
  </div>
</template>

<script>
import { interviewStatusType } from '@/enums';
import { clearGlobalStream } from '@huntica/huntica-interview-module/src/mediaHelpers';
import InterviewsQuotaExceededCandidate
  from '@/components/Payment/Quota/InterviewsQuotaExceededCandidate';
import useCandidates from '@/compositions/useCandidates';
import { toRefs } from '@vue/composition-api';
import AppWithLoading from '@/components/AppWithLoading';
import CandidateInterviewProcessing from '../CandidateInterviewProcessing.vue';
import CandidateInterviewOffice from '../CandidateInterviewOffice.vue';
import CandidateInterviewProcessingFailed from '../CandidateInterviewProcessingFailed.vue';
import OfficeInterviewRecorderForm from '../../OfficeInterview/OfficeInterviewRecorderForm.vue';
import DialogHeader from '../../DialogHeader.vue';

export default {
  name: 'OfficeInterview',
  components: {
    AppWithLoading,
    InterviewsQuotaExceededCandidate,
    CandidateInterviewProcessingFailed,
    CandidateInterviewOffice,
    CandidateInterviewProcessing,
    OfficeInterviewRecorderForm,
    DialogHeader,
  },
  props: {
    candidate: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    vacancy: {
      type: Object,
      required: true,
    },
    interview: {
      required: true,
    },
  },
  setup(props) {
    const { openCandidate } = useCandidates();
    const { candidate } = toRefs(props);

    async function openCurrentCandidate() {
      await openCandidate(candidate.value?.id);
    }

    return {
      openCurrentCandidate,
    };
  },
  data() {
    return {
      officeInterviewDialog: false,
      officeInterviewRecording: false,
      officeInterviewRecorded: false,
      officeInterviewResume: false,
      officeInterviewOverwrite: false,
      officeInterviewPrepareLoading: false,
      showOfficeInterviewCloseButton: true,
    };
  },
  computed: {
    officeInterviewPlayerTime: {
      get() {
        return this.$store.state.candidatePage.officeInterviewPlayerTime;
      },
      set(value) {
        this.$store.commit('candidatePage/setOfficeInterviewPlayerTime', value);
      },
    },
    showOfficeInterviewPlayer() {
      return this.interview
        && this.interview.status === interviewStatusType.READY;
    },
    isOfficeInterviewProcessing() {
      return this.interview
        && this.interview.status === interviewStatusType.PROCESSING;
    },
    isOfficeInterviewProcessingFailed() {
      return this.interview
        && this.interview.status === interviewStatusType.PROCESSING_FAILED;
    },
    showOfficeInterviewButtons() {
      return this.interview
        && (this.interview.status === interviewStatusType.RECORDING
        || this.interview.status === interviewStatusType.BLANK);
    },
  },
  beforeDestroy() {
    clearGlobalStream();
  },
  methods: {
    showOfficeInterviewDialog() {
      this.officeInterviewDialog = true;
    },
    overwriteOfficeInterview() {
      this.officeInterviewOverwrite = true;
      this.officeInterviewResume = false;
      this.showOfficeInterviewDialog();
    },
    resumeOfficeInterview() {
      this.officeInterviewOverwrite = false;
      this.officeInterviewResume = true;
      this.showOfficeInterviewDialog();
    },
    async saveOfficeInterview() {
      await this.withApiErrorHandling(async () => {
        await this.$store.dispatch('officeInterview/stopPublication', {
          id: this.candidate.officeInterview.id,
        });
      }, (e) => this.$toast.error(e.message), 'officeInterviewPrepareLoading');
    },
    interviewRecordingStarted() {
      this.officeInterviewRecording = true;
      this.showOfficeInterviewCloseButton = false;
    },
    interviewRecordingFinished() {
      this.officeInterviewDialog = false;
      this.officeInterviewRecording = false;
      this.officeInterviewRecorded = true;
      this.showOfficeInterviewCloseButton = true;
    },
    interviewRecordingError() {
      this.officeInterviewDialog = false;
      this.officeInterviewRecording = false;
      this.showOfficeInterviewCloseButton = true;
    },
    closeOfficeInterviewDialog() {
      this.officeInterviewDialog = false;
    },
  },
};
</script>

<style scoped>

</style>

<i18n locale="en" lang="yaml">
recordWasStopped: Recording was interrupted
chooseOption: Choose an option
resumeOfficeInterview: Continue recording
overwrite: Overwrite
save: Save
</i18n>

<i18n locale="ru" lang="yaml">
recordWasStopped: По какой-то причине запись была прервана
chooseOption: Выберите один из подходящих вариантов
resumeOfficeInterview: Продолжить запись
overwrite: Записать заново
save: Сохранить
</i18n>
